import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { 
  ClipboardCheck, 
  Truck, 
  Building, 
  Wrench,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ArrowLeft,
  Plus,
  Clock,
  MapPin,
  FileText,
  Loader2,
  ChevronRight,
  Download,
  Eye
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import OperatorEvaluation from '@/components/OperatorEvaluation';
import { useTranslation } from 'react-i18next';

// Icon mapping for inspection types
const iconMap = {
  crane: Building,
  truck: Truck,
  forklift: Truck,
  building: Building,
  ladder: Wrench
};

const WorkerInspections = ({ user }) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewInspection, setShowNewInspection] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetails, setTemplateDetails] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [viewInspection, setViewInspection] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    equipment_id: '',
    equipment_name: '',
    make_model: '',
    mileage_hours: '',
    location: '',
    shift: '1st',
    frequency: 'daily',
    notes: '',
    signature: ''
  });
  const [checklistResults, setChecklistResults] = useState({});
  const [currentStep, setCurrentStep] = useState('select'); // select, details, checklist, review

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templatesRes, inspectionsRes] = await Promise.all([
        axiosInstance.get('/inspections/all-templates'),
        axiosInstance.get('/inspections/my-inspections?limit=10')
      ]);
      // Combine built-in and custom templates
      setTemplates(templatesRes.data.all || []);
      setInspections(inspectionsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const startNewInspection = async (template) => {
    setSelectedTemplate(template);
    setCurrentStep('details');
    setShowNewInspection(true);
    
    // Load full template with checklist
    try {
      // Use different endpoint for built-in vs custom templates
      const endpoint = template.is_custom 
        ? `/inspections/custom-templates/${template.id}`
        : `/inspections/templates/${template.id}`;
      const res = await axiosInstance.get(endpoint);
      setTemplateDetails(res.data);
      
      // Initialize checklist results
      const initial = {};
      res.data.checklist.forEach(item => {
        initial[item.id] = { item_id: item.id, status: '', notes: '' };
      });
      setChecklistResults(initial);
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Failed to load inspection form');
    }
  };

  const handleChecklistChange = (itemId, status) => {
    setChecklistResults(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], status }
    }));
  };

  const handleChecklistNote = (itemId, notes) => {
    setChecklistResults(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], notes }
    }));
  };

  const getOverallStatus = () => {
    const results = Object.values(checklistResults);
    const hasFail = results.some(r => r.status === 'fail');
    return hasFail ? 'fail' : 'pass';
  };

  const isChecklistComplete = () => {
    if (!templateDetails) return false;
    return templateDetails.checklist.every(item => {
      const result = checklistResults[item.id];
      return result && result.status !== '';
    });
  };

  const submitInspection = async () => {
    if (!formData.location) {
      toast.error('Please enter the job site location');
      return;
    }
    
    if (!formData.signature || formData.signature.trim().length < 2) {
      toast.error('Please sign the inspection by typing your full name');
      return;
    }
    
    if (!isChecklistComplete()) {
      toast.error('Please complete all checklist items');
      return;
    }

    try {
      setSubmitting(true);
      
      const payload = {
        template_id: selectedTemplate.id,
        equipment_id: formData.equipment_id || null,
        equipment_name: formData.equipment_name || null,
        make_model: formData.make_model || null,
        mileage_hours: formData.mileage_hours || null,
        location: formData.location,
        shift: formData.shift || '1st',
        frequency: formData.frequency,
        checklist_results: Object.values(checklistResults),
        overall_status: getOverallStatus(),
        notes: formData.notes || null,
        signature: formData.signature,
        photos: []
      };

      await axiosInstance.post('/inspections', payload);
      
      toast.success('Inspection submitted successfully!');
      setShowNewInspection(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error submitting inspection:', error);
      toast.error(t('inspections.inspectionFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedTemplate(null);
    setTemplateDetails(null);
    setFormData({
      equipment_id: '',
      equipment_name: '',
      make_model: '',
      mileage_hours: '',
      location: '',
      shift: '1st',
      frequency: 'daily',
      notes: '',
      signature: ''
    });
    setChecklistResults({});
    setCurrentStep('select');
  };

  const closeDialog = () => {
    setShowNewInspection(false);
    resetForm();
  };

  const openViewDialog = (inspection) => {
    setViewInspection(inspection);
    setShowViewDialog(true);
  };

  const handleDownloadPdf = async (inspectionId) => {
    try {
      setDownloadingPdf(true);
      const response = await axiosInstance.get(`/inspections/export-pdf/${inspectionId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition?.match(/filename="(.+)"/)?.[1] || 'inspection.pdf';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Group checklist by category
  const groupedChecklist = templateDetails?.checklist.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {}) || {};

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('inspections.title')}</h1>
            <p className="text-gray-600">{t('inspections.completePreOp', 'Complete pre-operation safety inspections')}</p>
          </div>
        </div>

        <Tabs defaultValue="inspections" className="space-y-6">
          <TabsList>
            <TabsTrigger value="inspections">
              <ClipboardCheck className="w-4 h-4 mr-2" />
              {t('inspections.inspectionsTab', 'Inspections')}
            </TabsTrigger>
            <TabsTrigger value="evaluations" data-testid="worker-evaluations-tab">
              <FileText className="w-4 h-4 mr-2" />
              {t('inspections.evaluationsTab', 'Evaluations')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inspections">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inspections.filter(i => i.overall_status === 'pass').length}</p>
                <p className="text-sm text-gray-500">Passed</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inspections.filter(i => i.overall_status === 'fail').length}</p>
                <p className="text-sm text-gray-500">Failed</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Start New Inspection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Start New Inspection
            </CardTitle>
            <CardDescription>Select the type of equipment to inspect</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {templates.map(template => {
                const IconComponent = iconMap[template.icon] || ClipboardCheck;
                return (
                  <Button
                    key={template.id}
                    variant="outline"
                    className="h-auto p-4 flex items-start justify-between hover:border-red-300 hover:bg-red-50"
                    onClick={() => startNewInspection(template)}
                    data-testid={`inspection-template-${template.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${template.is_custom ? 'bg-purple-100' : 'bg-gray-100'}`}>
                        <IconComponent className={`h-5 w-5 ${template.is_custom ? 'text-purple-700' : 'text-gray-700'}`} />
                      </div>
                      <div className="text-left">
                        <p className="font-medium flex items-center gap-2">
                          {template.name}
                          {template.is_custom && (
                            <Badge variant="secondary" className="text-xs">Custom</Badge>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">{template.checklist_count} items</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recent Inspections */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Inspections</CardTitle>
          </CardHeader>
          <CardContent>
            {inspections.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{t('inspections.noInspections')}</p>
            ) : (
              <div className="space-y-3">
                {inspections.map(inspection => (
                  <div
                    key={inspection.id}
                    className="p-3 border rounded-lg flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => openViewDialog(inspection)}
                    data-testid={`view-inspection-${inspection.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${inspection.overall_status === 'pass' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {inspection.overall_status === 'pass' ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{inspection.template_name}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <MapPin className="h-3 w-3" />
                          {inspection.location}
                          <span>•</span>
                          <Clock className="h-3 w-3" />
                          {new Date(inspection.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={inspection.overall_status === 'pass' ? 'default' : 'destructive'}>
                        {inspection.overall_status === 'pass' ? 'Passed' : 'Failed'}
                      </Badge>
                      <Eye className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="evaluations">
            <OperatorEvaluation user={user} />
          </TabsContent>
        </Tabs>
      </main>

      {/* New Inspection Dialog */}
      <Dialog open={showNewInspection} onOpenChange={closeDialog}>
        <DialogContent className="max-w-2xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>
              {selectedTemplate?.name || 'New Inspection'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6">
            {/* Step: Details */}
            {currentStep === 'details' && (
              <div className="space-y-4 py-4">
                {/* Operator Info - Auto filled */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>Operator:</strong> {user?.name || user?.email || 'Unknown'} | 
                    <strong> Date:</strong> {new Date().toLocaleDateString()} | 
                    <strong> Time:</strong> {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Equipment ID / Asset # *</Label>
                    <Input
                      placeholder="e.g., LIFT-001"
                      value={formData.equipment_id}
                      onChange={(e) => setFormData({...formData, equipment_id: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Equipment Name / Description</Label>
                    <Input
                      placeholder="e.g., JLG 450AJ Boom Lift"
                      value={formData.equipment_name}
                      onChange={(e) => setFormData({...formData, equipment_name: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Make / Model</Label>
                    <Input
                      placeholder="e.g., JLG / 450AJ"
                      value={formData.make_model}
                      onChange={(e) => setFormData({...formData, make_model: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Hour Meter / Mileage Reading</Label>
                    <Input
                      placeholder="e.g., 1,234 hrs or 45,678 mi"
                      value={formData.mileage_hours}
                      onChange={(e) => setFormData({...formData, mileage_hours: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Shift</Label>
                    <Select value={formData.shift} onValueChange={(v) => setFormData({...formData, shift: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1st">1st Shift (Day)</SelectItem>
                        <SelectItem value="2nd">2nd Shift (Swing)</SelectItem>
                        <SelectItem value="3rd">3rd Shift (Night)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Inspection Type</Label>
                    <Select value={formData.frequency} onValueChange={(v) => setFormData({...formData, frequency: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Pre-Operation (Daily)</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Job Site / Location *</Label>
                  <Input
                    placeholder="e.g., Site A - Building 2, Level 3"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    required
                  />
                </div>

                <Button
                  className="w-full bg-red-600 hover:bg-red-700"
                  onClick={() => setCurrentStep('checklist')}
                  disabled={!formData.location}
                >
                  Continue to Checklist
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}

            {/* Step: Checklist */}
            {currentStep === 'checklist' && templateDetails && (
              <div className="space-y-6 py-4">
                {Object.entries(groupedChecklist).map(([category, items]) => (
                  <div key={category}>
                    <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {category}
                    </h3>
                    <div className="space-y-3">
                      {items.map(item => (
                        <div key={item.id} className="p-3 border rounded-lg">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <p className="text-sm">
                                {item.item}
                                {item.critical && (
                                  <Badge variant="destructive" className="ml-2 text-xs">Critical</Badge>
                                )}
                              </p>
                            </div>
                            <RadioGroup
                              value={checklistResults[item.id]?.status || ''}
                              onValueChange={(v) => handleChecklistChange(item.id, v)}
                              className="flex gap-2"
                            >
                              <div className="flex items-center">
                                <RadioGroupItem value="pass" id={`${item.id}-pass`} className="peer sr-only" />
                                <Label
                                  htmlFor={`${item.id}-pass`}
                                  className={`px-3 py-1 border rounded cursor-pointer text-sm ${
                                    checklistResults[item.id]?.status === 'pass'
                                      ? 'bg-green-100 border-green-500 text-green-700'
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  Pass
                                </Label>
                              </div>
                              <div className="flex items-center">
                                <RadioGroupItem value="fail" id={`${item.id}-fail`} className="peer sr-only" />
                                <Label
                                  htmlFor={`${item.id}-fail`}
                                  className={`px-3 py-1 border rounded cursor-pointer text-sm ${
                                    checklistResults[item.id]?.status === 'fail'
                                      ? 'bg-red-100 border-red-500 text-red-700'
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  Fail
                                </Label>
                              </div>
                              <div className="flex items-center">
                                <RadioGroupItem value="na" id={`${item.id}-na`} className="peer sr-only" />
                                <Label
                                  htmlFor={`${item.id}-na`}
                                  className={`px-3 py-1 border rounded cursor-pointer text-sm ${
                                    checklistResults[item.id]?.status === 'na'
                                      ? 'bg-gray-200 border-gray-400 text-gray-700'
                                      : 'hover:bg-gray-50'
                                  }`}
                                >
                                  N/A
                                </Label>
                              </div>
                            </RadioGroup>
                          </div>
                          {checklistResults[item.id]?.status === 'fail' && (
                            <div className="mt-2">
                              <Input
                                placeholder="Describe the issue..."
                                value={checklistResults[item.id]?.notes || ''}
                                onChange={(e) => handleChecklistNote(item.id, e.target.value)}
                                className="text-sm"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                <div>
                  <Label>Additional Notes / Defects Found</Label>
                  <Textarea
                    placeholder="Describe any defects, issues, or observations..."
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows={3}
                  />
                </div>

                {/* Operator Certification */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">Operator Certification</h4>
                  <p className="text-sm text-blue-800 mb-3">
                    By typing my name below, I certify that I have personally inspected this equipment 
                    and the information recorded is accurate. I understand that operating defective 
                    equipment is prohibited.
                  </p>
                  <div>
                    <Label>Operator Signature (Type Full Name) *</Label>
                    <Input
                      placeholder="Type your full name to sign"
                      value={formData.signature}
                      onChange={(e) => setFormData({...formData, signature: e.target.value})}
                      className="bg-white"
                    />
                  </div>
                </div>

                {/* Summary */}
                <div className={`p-4 rounded-lg ${getOverallStatus() === 'pass' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    {getOverallStatus() === 'pass' ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    )}
                    <span className="font-medium">
                      {getOverallStatus() === 'pass' 
                        ? 'Equipment passes inspection' 
                        : 'Equipment has failed items - DO NOT USE'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-gray-50">
            {currentStep === 'checklist' && (
              <>
                <Button variant="outline" onClick={() => setCurrentStep('details')}>
                  Back
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700"
                  onClick={submitInspection}
                  disabled={submitting || !isChecklistComplete()}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Inspection'
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Inspection Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              {viewInspection?.overall_status === 'pass' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              {viewInspection?.template_name}
            </DialogTitle>
            <DialogDescription className="flex items-center justify-between">
              <span>Inspection details and checklist results</span>
              <Button 
                size="sm" 
                variant="outline" 
                data-testid="worker-download-inspection-pdf-btn"
                onClick={() => handleDownloadPdf(viewInspection?.id)}
                disabled={downloadingPdf}
                className="ml-2"
              >
                {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
                Download PDF
              </Button>
            </DialogDescription>
          </DialogHeader>

          {viewInspection && (
            <div className="flex-1 overflow-y-auto px-6">
              <div className="space-y-6 py-4">
                {/* Summary */}
                <div className={`p-4 rounded-lg ${viewInspection.overall_status === 'pass' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold">
                        {viewInspection.overall_status === 'pass' ? 'Inspection Passed' : 'Inspection Failed'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(viewInspection.created_at).toLocaleString()}
                      </p>
                    </div>
                    <Badge>{viewInspection.frequency || 'daily'}</Badge>
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-sm text-gray-500">Inspector</p><p className="font-medium">{viewInspection.inspector_name}</p></div>
                  <div><p className="text-sm text-gray-500">Location</p><p className="font-medium">{viewInspection.location}</p></div>
                  {viewInspection.equipment_id && (
                    <div><p className="text-sm text-gray-500">Equipment ID</p><p className="font-medium">{viewInspection.equipment_id}</p></div>
                  )}
                  {viewInspection.equipment_name && (
                    <div><p className="text-sm text-gray-500">Equipment</p><p className="font-medium">{viewInspection.equipment_name}</p></div>
                  )}
                </div>

                {/* Checklist Results */}
                <div>
                  <h3 className="font-semibold mb-3">Checklist Results</h3>
                  {(() => {
                    const results = viewInspection.checklist_results || [];
                    const categories = {};
                    results.forEach(r => {
                      const cat = r.category || 'General';
                      if (!categories[cat]) categories[cat] = [];
                      categories[cat].push(r);
                    });
                    return Object.entries(categories).map(([category, items]) => (
                      <div key={category} className="mb-4">
                        <div className="bg-gray-800 text-white text-sm font-medium px-3 py-1.5 rounded-t">
                          {category}
                        </div>
                        <div className="border border-t-0 rounded-b divide-y">
                          {items.map((result, idx) => (
                            <div key={idx} className={`flex items-start gap-3 px-3 py-2 ${result.status === 'fail' ? 'bg-red-50' : ''}`}>
                              {result.status === 'pass' && <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />}
                              {result.status === 'fail' && <XCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />}
                              {result.status === 'na' && <div className="h-4 w-4 rounded-full bg-gray-300 mt-0.5 flex-shrink-0" />}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm flex items-center gap-1 flex-wrap">
                                  <span>{result.item_text || result.item_id}</span>
                                  {result.critical && (
                                    <Badge variant="destructive" className="text-[10px] px-1 py-0">CRITICAL</Badge>
                                  )}
                                </div>
                                {result.notes && (
                                  <p className="text-xs text-red-600 mt-1">Note: {result.notes}</p>
                                )}
                              </div>
                              <Badge 
                                variant={result.status === 'pass' ? 'default' : result.status === 'fail' ? 'destructive' : 'secondary'} 
                                className="text-xs flex-shrink-0"
                              >
                                {result.status.toUpperCase()}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                {/* Notes */}
                {viewInspection.notes && (
                  <div>
                    <h3 className="font-semibold mb-2">Additional Notes</h3>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{viewInspection.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkerInspections;
